import { __ } from '@wordpress/i18n';
import {
  useBlockProps,
  BlockControls,
  MediaReplaceFlow,
  MediaUploadCheck,
} from '@wordpress/block-editor';

import Inspector from './InspectorControls';
import { resolvePresetValue } from '../../utils/helpers';

/**
 * Editor for a single avatar.
 *
 * Reads the parent context (size/shape/ring) so the preview matches the
 * frontend, and falls back to initials derived from `name` when no image is
 * set. Selecting the block no longer auto-opens the media dialog; the media
 * library is opened from the block toolbar (a floating popover), so a click
 * just selects the avatar.
 */
export function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  return parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
}

export default function Edit({ attributes, setAttributes, context }) {
  const { url, alt, name } = attributes;
  const size = context['brandy/avatarSize'] || 40;
  const shape = context['brandy/avatarShape'] || 'circle';
  /*
   * The parent passes its stored attribute down verbatim, so a palette pick
   * arrives as `var:preset|color|<slug>` — a reference, not CSS. render.php
   * expands it with Helpers::get_ref_color(); without the same expansion here
   * the canvas assigns the raw reference to the custom property, the browser
   * discards the declaration, and the ring falls back to the stylesheet default
   * while the frontend shows the chosen colour.
   */
  const ringColor =
    resolvePresetValue(context['brandy/avatarRingColor']) || '#ffffff';
  const ringWidth = context['brandy/avatarRingWidth'] ?? 2;

  const onSelectImage = (media) => {
    setAttributes({
      mediaId: media.id,
      url: media.url,
      alt: media.alt || attributes.alt || '',
    });
  };

  // Passing onSelectURL is what makes MediaReplaceFlow show its own
  // "Insert from URL" field; without it the toolbar offers the library only.
  // An external image has no attachment row, so the id resets to 0.
  const onSelectImageUrl = (value) => {
    setAttributes({ mediaId: 0, url: value });
  };

  const blockProps = useBlockProps({
    className: `brandy-avatar is-shape-${shape}`,
    style: {
      '--brandy-avatar-size': `${size}px`,
      '--brandy-avatar-ring-color': ringColor,
      '--brandy-avatar-ring-width': `${ringWidth}px`,
    },
  });

  return (
    <>
      <Inspector attributes={attributes} setAttributes={setAttributes} />

      <BlockControls>
        <MediaUploadCheck>
          <MediaReplaceFlow
            mediaId={attributes.mediaId}
            mediaURL={url}
            allowedTypes={['image']}
            accept="image/*"
            onSelect={onSelectImage}
            onSelectURL={onSelectImageUrl}
            name={url ? __('Replace image', 'brandy-blocks') : __('Add image', 'brandy-blocks')}
          />
        </MediaUploadCheck>
      </BlockControls>

      <div {...blockProps}>
        {url ? (
          <img className="brandy-avatar__img" src={url} alt={alt || name} />
        ) : (
          <span className="brandy-avatar__initials" aria-hidden="true">
            {getInitials(name)}
          </span>
        )}
      </div>
    </>
  );
}
